import { useNotify } from 'hooks/use-notify';
import { useTranslate } from 'hooks/use-translate';
import React, { HTMLProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  cropImg,
  ErrorFile,
  ErrorFileCode,
  fileToBase64,
  getFileAccept,
  validateFileSize,
  validateFileType,
  ValueFileUploader,
} from 'utils/file-uploader';

interface BaseProps {
  accept?: string[];
  maxSize?: number;
  imgCrop?: {
    width: number;
    height: number;
  };
}

interface MultipleProps extends BaseProps {
  multiple: true;
  onChange?: (v: ValueFileUploader[], files: File[]) => void;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  onChange?: (v: ValueFileUploader, file: File) => void;
}

type Props = MultipleProps | SingleProps;

export const useFileUploader = (p: Props) => {
  const { accept, maxSize = 2, imgCrop } = p;
  const ref = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<null | ErrorFile>(null);

  const { inputType, contentTypes } = useMemo(() => getFileAccept(accept), [accept]);

  const onChangeWrap = useCallback(
    (v: ValueFileUploader[], files: File[]) => {
      if (p.multiple) {
        p.onChange && p.onChange(v, files);
      } else {
        p.onChange && p.onChange(v[0] || '', files[0]);
      }
    },
    [p],
  );

  const _onChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(null);
      let { files } = e.target;
      if (!files) {
        return;
      }
      try {
        const result = await Promise.all(
          Array.from(files).map(async (file) => {
            await validateFileType(file, contentTypes);
            await validateFileSize(file, maxSize);

            let value = imgCrop
              ? await cropImg(file, imgCrop.width, imgCrop.height)
              : await fileToBase64(file);

            return { name: file.name, size: file.size, type: file.type, value };
          }),
        );
        onChangeWrap(result, Array.from(files || []));
      } catch (e: any) {
        setError(e);
      }
    },
    [contentTypes, maxSize, onChangeWrap, imgCrop],
  );

  const inputProps = useMemo<HTMLProps<HTMLInputElement>>(() => {
    return {
      ref,
      type: 'file',
      hidden: true,
      onChange: _onChange,
      accept: inputType,
      multiple: p.multiple,
      onClick: () => {
        if (ref.current) {
          ref.current.value = '';
        }
      },
    };
  }, [inputType, _onChange, p.multiple]);

  const onOpen = useCallback(() => {
    ref.current?.click();
  }, []);
  const onRemove = useCallback(() => {
    if (ref.current) {
      ref.current.value = '';
      onChangeWrap([], []);
    }
  }, [onChangeWrap]);

  return { inputProps, onOpen, onRemove, error };
};
export const useFileUploaderNotifyError = (
  error: ErrorFile | null,
  option: { maxSize: number; accept: string[] },
) => {
  const { maxSize, accept: _accept } = option;
  const accept = String(_accept);
  const { tp } = useTranslate();
  const { onError } = useNotify();

  useEffect(() => {
    if (error?.code === ErrorFileCode.MAX_SIZE) {
      toast.error(tp('file-error-size', { maxSize }));
    } else if (error?.code === ErrorFileCode.FILE_TYPE) {
      toast?.error(tp('file-error-type', { accept }));
    } else if (error) {
      onError(error);
    }
  }, [error, tp, accept, maxSize, onError]);
};
